import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Banknote, Lock } from "lucide-react";
import { toast } from "sonner";

// Demo accounts displayed on the login page for evaluation.
// These mirror the seed users created server-side in auth.py:seed_admin_and_demo_users().
// They are NOT secrets — they are intentionally visible to anyone using the demo,
// so the UI can present a one-click sign-in. For production, remove this list and
// the rendering block below.
const DEMO_ACCOUNTS = [
  { email: "admin@bank.id", password: "admin123", label: "Administrator" },
  { email: "credit@bank.id", password: "credit123", label: "Credit Officer" },
  { email: "risk@bank.id", password: "risk123", label: "Risk Officer" },
  { email: "approver@bank.id", password: "approver123", label: "Approver" },
  { email: "viewer@bank.id", password: "viewer123", label: "Viewer" },
];

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("admin@bank.id");
  const [password, setPassword] = useState("admin123");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Login berhasil");
      navigate("/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login gagal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: branding */}
      <div className="hidden lg:flex flex-col justify-between bg-zinc-950 text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBiYW5raW5nJTIwb2ZmaWNlJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzc5MjAyMDMzfDA&ixlib=rb-4.1.0&q=85"
            alt="bank"
            className="w-full h-full object-cover grayscale"
          />
        </div>
        <div className="relative z-10 flex items-center gap-2">
          <Banknote className="h-7 w-7" />
          <span className="text-lg font-bold tracking-tight">LIMIT.GRAPH</span>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-400 mb-3">
            Risk Management Suite
          </div>
          <h2 className="text-4xl font-bold leading-tight">
            Memantau eksposur, limit, dan jaringan keterhubungan obligor — dalam satu graf.
          </h2>
          <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
            Dashboard credit risk berbasis pendekatan graph untuk perbankan: limit, fasilitas, collateral, guarantor, covenant, dan early warning system.
          </p>
        </div>
        <div className="relative z-10 text-xs text-zinc-500">
          © 2026 Limit.Graph · Banking Risk Platform
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="flex items-center gap-2 lg:hidden mb-6">
              <Banknote className="h-6 w-6" />
              <span className="text-base font-bold">LIMIT.GRAPH</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Masuk ke akun Anda</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Gunakan akun bank Anda atau pilih akun demo di bawah.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider">Email</Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider">Password</Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password-input"
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit" disabled={submitting}
              className="w-full bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="login-submit-button"
            >
              <Lock className="h-3.5 w-3.5 mr-2" />
              {submitting ? "Memproses…" : "Masuk"}
            </Button>
          </form>

          <div className="mt-8">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
              Akun demo
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ACCOUNTS.map((acc) => (
                <button
                  key={acc.email}
                  onClick={() => { setEmail(acc.email); setPassword(acc.password); }}
                  className="text-left p-2.5 border border-border rounded-md hover:border-foreground/30 transition-colors"
                  data-testid={`demo-account-${acc.email.split("@")[0]}`}
                >
                  <div className="text-xs font-semibold">{acc.label}</div>
                  <div className="text-[10px] text-muted-foreground mono">{acc.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
